/**
 * Google Analytics 4 integration for the SAS site.
 *
 * Why a custom thin wrapper instead of `react-ga4` / similar?
 *
 *   1. Zero new dependencies — keeps the bundle lean.
 *   2. Lazy script injection — the gtag.js loader is `async` and only
 *      runs in browser environments where the Measurement ID is set, so
 *      the script never blocks first paint and is a hard no-op in
 *      development or any environment that hasn't configured the ID.
 *   3. SPA-safe page views — react-router uses the History API, so
 *      gtag's default auto-pageview only fires once. We disable that
 *      and fire one `page_view` per `useLocation()` change instead.
 *   4. Bilingual context — every event automatically includes the
 *      derived `language` ("ar" or "en") and the page path, so
 *      reports can be sliced by locale without extra setup in GA4.
 *
 * Privacy posture:
 *   - `anonymize_ip: true`
 *   - `allow_google_signals: false` (no cross-device tracking by default)
 *   - `allow_ad_personalization_signals: false`
 *   - `cookie_flags: "SameSite=None;Secure"` — required for cross-site
 *     iframe scenarios and aligned with modern browser defaults.
 *
 * Safety:
 *   - All functions are no-ops when `REACT_APP_GA_MEASUREMENT_ID` is
 *     missing/empty, when `window` is undefined (SSR / tests), or when
 *     `localhost` / `127.0.0.1` is the current host.
 *   - `trackEvent` swallows its own throws so a misbehaving GA library
 *     can never break a user interaction (e.g. a tool's "Try Tool"
 *     click still navigates even if gtag has failed to load).
 */

// CRA's DefinePlugin replaces `process.env.REACT_APP_*` references with
// the literal string at build time, so this resolves to e.g.
// "G-QC34GM1WH7" || "" → "G-QC34GM1WH7" in the bundle. Importantly we
// do NOT wrap this in a `typeof process !== "undefined"` guard, because
// `process` itself isn't a real global in the browser — the guard
// short-circuits to `false`, which would leave MEASUREMENT_ID
// permanently empty in production.
const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

let _initialized = false;
let _disabled = false;

/* ---------------------------------------------------------------------- */
/* Internal helpers                                                       */
/* ---------------------------------------------------------------------- */

function hasWindow() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function isLocalHost() {
  if (!hasWindow()) return false;
  const h = window.location && window.location.hostname;
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h === "0.0.0.0" ||
    h === "::1" ||
    (typeof h === "string" && h.endsWith(".local"))
  );
}

/**
 * Derive language from the current URL pathname. Mirrors
 * `i18n/LanguageContext.langPrefix` but does NOT require the React
 * context to be mounted — this module runs *outside* LanguageProvider
 * at the BrowserRouter root, so we deduce the locale from the path.
 */
export function langFromPath(pathname) {
  if (!pathname) return "en";
  return pathname === "/ar" || pathname.startsWith("/ar/") ? "ar" : "en";
}

/* ---------------------------------------------------------------------- */
/* Init                                                                   */
/* ---------------------------------------------------------------------- */

/**
 * Inject the gtag.js loader asynchronously and configure GA4.
 *
 *  - Safe to call multiple times — only the first call has any effect.
 *  - No-op when the Measurement ID is missing or we're on localhost.
 *  - We disable `send_page_view` so that the SPA route hook is the
 *    single source of page_view events, avoiding double counting.
 */
export function initAnalytics() {
  if (_initialized || _disabled) return;
  if (!hasWindow()) return;
  if (!MEASUREMENT_ID) {
    _disabled = true;
    return;
  }
  // Skip on developer machines to keep dev clicks out of the property.
  if (isLocalHost()) {
    _disabled = true;
    return;
  }

  // gtag's dataLayer + shim function MUST be defined synchronously
  // BEFORE the loader script executes — gtag will then replay any
  // queued commands. This is exactly the snippet Google recommends.
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== "function") {
    // eslint-disable-next-line prefer-rest-params
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }

  // Inject the async script tag. `crossorigin="anonymous"` lets the
  // browser cache it cross-origin like any other static resource.
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(
    MEASUREMENT_ID
  )}`;
  script.setAttribute("data-source", "sas-ga4");
  document.head.appendChild(script);

  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    // SPA — we fire page_view manually on every route change.
    send_page_view: false,
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_flags: "SameSite=None;Secure",
    // Tagging the property as bilingual makes it easy to slice reports
    // by locale without writing custom dimensions later.
    language: langFromPath(window.location.pathname),
  });

  _initialized = true;
}

/* ---------------------------------------------------------------------- */
/* Page view + generic event                                              */
/* ---------------------------------------------------------------------- */

function safeGtag(...args) {
  try {
    if (!_initialized) return;
    if (typeof window === "undefined") return;
    if (typeof window.gtag !== "function") return;
    window.gtag(...args);
  } catch {
    // Never let analytics throw into product code.
  }
}

/**
 * Manually fire a GA4 `page_view` event. Called by the SPA route hook.
 * We include the current `document.title`, full URL, and derived
 * language so the event is self-contained even if the user's network
 * blocks the GA collect endpoint mid-session.
 */
export function trackPageView({ pathname = "", search = "", title = "" } = {}) {
  if (!hasWindow()) return;
  if (!_initialized) return;
  const path = `${pathname}${search}`;
  safeGtag("event", "page_view", {
    page_path: path,
    page_location:
      (window.location && window.location.origin ? window.location.origin : "") +
      path,
    page_title: title || (document && document.title) || "",
    language: langFromPath(pathname),
  });
}

/**
 * Track a custom event. `params` is a flat object that maps to GA4
 * event parameters (string / number / boolean values only).
 *
 * Examples:
 *   trackEvent("tool_view", { tool_slug: "chatgpt-free" });
 *   trackEvent("save_tool", { tool_slug, action: "added" });
 */
export function trackEvent(name, params = {}) {
  if (!name || typeof name !== "string") return;
  if (!_initialized) return;
  if (typeof window === "undefined") return;
  // GA4 has a 40-char event name limit and 25 param max per event.
  // We don't enforce here because all our call-sites stay well within
  // that envelope, but we do strip undefined values so they don't
  // appear as the literal string "undefined" in GA4 reports.
  const clean = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null) clean[k] = v;
  }
  // Auto-tag with locale derived from current URL.
  if (clean.language === undefined && hasWindow()) {
    clean.language = langFromPath(window.location.pathname);
  }
  safeGtag("event", name, clean);
}

/* ---------------------------------------------------------------------- */
/* React hook + bridge component                                          */
/* ---------------------------------------------------------------------- */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Fire a GA4 `page_view` on every react-router location change.
 *
 * Implementation notes:
 *   - The first pageview is intentionally delayed via `requestAnimationFrame`
 *     so that each route's component has had a chance to update
 *     `document.title` (most pages do this in a `useEffect`). Without
 *     this, the very first page_view would report the previous page's
 *     title.
 *   - We fire on every change of `pathname` OR `search` so e.g. a
 *     filter change on `/tools?cats=research` is reported as its own
 *     pageview, which is the standard GA4 SPA expectation.
 */
export function useAnalyticsPageviews() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (!_initialized) return;

    // Defer one frame so document.title is settled by per-page hooks.
    let cancelled = false;
    const raf =
      typeof window !== "undefined" && window.requestAnimationFrame
        ? window.requestAnimationFrame
        : (cb) => setTimeout(cb, 16);

    raf(() => {
      if (cancelled) return;
      trackPageView({
        pathname,
        search,
        title: typeof document !== "undefined" ? document.title : "",
      });
    });

    return () => {
      cancelled = true;
    };
  }, [pathname, search]);
}

/**
 * Tiny presentational component that simply mounts the hook. Lets us
 * drop `<AnalyticsBridge />` once at the BrowserRouter root.
 */
export function AnalyticsBridge() {
  useAnalyticsPageviews();
  return null;
}

/* ---------------------------------------------------------------------- */
/* Test helpers (no-op in production)                                     */
/* ---------------------------------------------------------------------- */

/** Returns whether analytics has been initialised. Useful for unit
 *  tests and debug overlays. Not exported as part of the public API. */
export function _isInitializedForTest() {
  return _initialized;
}
